import type {
  AuthUser,
  UpdateUserProfileInput,
  UserListItem,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type NormalizeAuthUser = (value: unknown) => AuthUser;
type NormalizeUserListItem = (value: unknown) => UserListItem;
type ToSafeNonNegativeInt = (value: unknown, fallback: number) => number;

interface UserApiModuleDependencies {
  extractApiError: ExtractApiError;
  normalizeAuthUser: NormalizeAuthUser;
  normalizeUserListItem: NormalizeUserListItem;
  toSafeNonNegativeInt: ToSafeNonNegativeInt;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class UserApiModule {
  constructor(
    private readonly context: Pick<WarehouseApiModuleContext, "sendJson">,
    private readonly deps: UserApiModuleDependencies,
  ) {}

  async getUserById(userId: string): Promise<AuthUser> {
    const payload = await this.context.sendJson<unknown>(
      `/api/users/${encodeURIComponent(userId)}`,
      "GET",
    );

    if (!isRecord(payload)) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке профиля",
      );
    }

    return this.deps.normalizeAuthUser(payload);
  }

  async updateUserProfile(
    userId: string,
    input: UpdateUserProfileInput,
  ): Promise<AuthUser> {
    const payload = await this.context.sendJson<unknown>(
      `/api/users/${encodeURIComponent(userId)}`,
      "PUT",
      {
        ...(typeof input.name === "string" ? { name: input.name.trim() } : {}),
        ...(typeof input.photo === "string"
          ? { photo: input.photo.trim() }
          : {}),
      },
    );

    if (!isRecord(payload)) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при обновлении профиля",
      );
    }

    return this.deps.normalizeAuthUser(payload);
  }

  async getUsers(): Promise<UserListItem[]> {
    const payload = await this.context.sendJson<unknown>("/api/users", "GET");

    if (Array.isArray(payload)) {
      return payload.map((item) => this.deps.normalizeUserListItem(item));
    }

    if (isRecord(payload) && Array.isArray(payload.data)) {
      return payload.data.map((item) => this.deps.normalizeUserListItem(item));
    }

    const apiError = this.deps.extractApiError(payload);
    throw new Error(
      apiError || "Некорректный ответ сервера при загрузке пользователей",
    );
  }

  async getPendingUsersCount(): Promise<number> {
    const payload = await this.context.sendJson<unknown>(
      "/api/users/pending-count",
      "GET",
    );
    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !isRecord(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError ||
          "Некорректный ответ сервера при получении количества заявок",
      );
    }

    return this.deps.toSafeNonNegativeInt(payload.data.pendingUsers, 0);
  }

  async approveUser(userId: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/users/${encodeURIComponent(userId)}/approve`,
      "POST",
    );

    if (!isRecord(payload) || payload.success !== true) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || "Не удалось одобрить пользователя");
    }
  }

  async deleteUser(userId: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/users/${encodeURIComponent(userId)}`,
      "DELETE",
    );

    if (isRecord(payload) && (payload.success === true || payload.message)) {
      return;
    }

    const apiError = this.deps.extractApiError(payload);
    throw new Error(apiError || "Не удалось удалить пользователя");
  }
}
